/**
 * Where a `res://` path lives on a host: under the Godot project root, the nearest directory, from a file's own
 * upward, that holds `project.godot`, or under the file's own directory when none does. No `node:path`, since the
 * VS Code host runs in a web worker. String paths come back with forward slashes, which every host's filesystem API
 * accepts.
 */
import { PROJECT_FILE_NAME } from '../godot/project/project.js';
import { pathJoin } from '../godot/string/string.js';
export const RES_SCHEME = 'res://';
/**
 * `path` without its trailing slashes. A loop, not `/\/+$/`: that regex backtracks quadratically on a
 * path with many slashes that do not end it, and a host hands this any path it is given.
 */
function withoutTrailingSlashes(path) {
    let end = path.length;
    while (end > 0 && path[end - 1] === '/')
        end--;
    return path.slice(0, end);
}
/** `path` with forward slashes and no trailing one, unless it is a filesystem root such as `/` or `c:/`. */
export function forwardSlashes(path) {
    const trimmed = withoutTrailingSlashes(path.replace(/\\/g, '/'));
    return trimmed === '' || /^[A-Za-z]:$/.test(trimmed) ? `${trimmed}/` : trimmed;
}
/**
 * The path case of the default filesystem on `platform`, a Node `process.platform` value. Windows and macOS ignore
 * case. Any other platform, and an unknown one such as a web worker's, counts case: a refused path is safer than a
 * sibling outside the root.
 */
export function pathCaseOf(platform) {
    return platform === 'win32' || platform === 'darwin' ? 'insensitive' : 'sensitive';
}
/**
 * `path` as a filesystem with `pathCase` compares it, for a map keyed by path: two paths that name one place share a
 * key. Forward slashes, and lowercased on a filesystem that ignores case.
 */
export function pathKey(path, pathCase) {
    const slashed = forwardSlashes(path);
    return pathCase === 'insensitive' ? slashed.toLowerCase() : slashed;
}
/** Whether `a` and `b` name one path on a filesystem with `pathCase`. */
export function isSamePath(a, b, pathCase) {
    return pathKey(a, pathCase) === pathKey(b, pathCase);
}
/**
 * Whether `candidate` is `root` or under it. The separator is the boundary, so `/proj-other` is not under `/proj`.
 * Case counts unless `pathCase` is insensitive, so on Linux `/w/game` is not under `/w/Game`.
 */
export function isWithinRoot(root, candidate, pathCase) {
    const rootKey = pathKey(root, pathCase);
    const candidateKey = pathKey(candidate, pathCase);
    if (candidateKey === rootKey)
        return true;
    const prefix = rootKey.endsWith('/') ? rootKey : `${rootKey}/`;
    return candidateKey.startsWith(prefix);
}
/** `relative` under `dir`, with one separator between them. */
function joinUnder(dir, relative) {
    return pathJoin(forwardSlashes(dir), relative);
}
/**
 * The directory holding `path`, or null for a filesystem root or a bare name: the top of an upward walk over string
 * paths, as `findResRoot`'s `parent`.
 */
export function parentDir(path) {
    const dir = forwardSlashes(path);
    const lastSlash = dir.lastIndexOf('/');
    return dir.endsWith('/') || lastSlash < 0 ? null : forwardSlashes(dir.slice(0, lastSlash));
}
/**
 * `relative` with `.` and empty segments dropped and each `..` folded into its parent, or null when a `..` climbs
 * above where the path starts.
 */
export function normalizeRelativePath(relative) {
    const segments = [];
    for (const segment of relative.replace(/\\/g, '/').split('/')) {
        if (segment === '' || segment === '.')
            continue;
        if (segment !== '..')
            segments.push(segment);
        else if (segments.pop() === undefined)
            return null;
    }
    return segments.join('/');
}
/**
 * `paths` in code-unit order, so every host lists them alike: `localeCompare` would order them by
 * the host's locale.
 */
export function inPathOrder(paths) {
    return [...paths].sort();
}
/**
 * `resPath` relative to its project root, or null for a path that is not `res://` or that escapes the root. For a host
 * whose paths are not strings, such as VS Code's `Uri`.
 */
export function resRelativePath(resPath) {
    return resPath.startsWith(RES_SCHEME) ? normalizeRelativePath(resPath.slice(RES_SCHEME.length)) : null;
}
/** The host path of `resPath` under `projectRoot`, or null for a path that is not `res://` or that escapes the root. */
export function resolveResPath(projectRoot, resPath) {
    const relative = resRelativePath(resPath);
    return relative === null ? null : joinUnder(projectRoot, relative);
}
/**
 * The nearest directory, from `start` upward through `parent`, where `hasProjectFile` finds a `project.godot`, or null
 * for none. The walk ends only at the top, where `parent` gives null, so no host's workspace bounds it. Generic over
 * the directory handle, so each host walks its own kind: a string path, or a `Uri` that keeps its scheme.
 */
async function findProjectRoot(start, parent, hasProjectFile) {
    for (let dir = start; dir !== null; dir = parent(dir)) {
        if (await hasProjectFile(dir))
            return dir;
    }
    return null;
}
/**
 * The directory a scene's `res://` paths resolve under: its Godot project root, or `sceneDir` itself when no
 * directory holds `project.godot` and `isInWorkspace(sceneDir)`. Null otherwise: a listing from a loose scene in
 * `/tmp` or a home directory would read every file under it. Godot has no `res://` outside a project, but a loose
 * scene, such as one copied out of a demo, names its files from its own directory. Every host takes this one answer.
 */
export async function findResRoot(sceneDir, parent, hasProjectFile, isInWorkspace) {
    const project = await findProjectRoot(sceneDir, parent, hasProjectFile);
    if (project !== null)
        return project;
    return isInWorkspace(sceneDir) ? sceneDir : null;
}
/** The `project.godot` a directory holds, for a host's `hasProjectFile`. */
export function projectFileIn(dir) {
    return joinUnder(dir, PROJECT_FILE_NAME);
}
//# sourceMappingURL=resPath.js.map